import { useState, useMemo, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  alpha,
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import PaymentIcon from '@mui/icons-material/Payment';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import MoneyIcon from '@mui/icons-material/Money';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import UndoIcon from '@mui/icons-material/Undo';
import BusinessIcon from '@mui/icons-material/Business';
import DescriptionIcon from '@mui/icons-material/Description';
import axiosInstance from '../../lib/axios';
import { TableToolbar, FilterOption, ColumnConfig } from '../../components/TableToolbar';
import { getUser } from '../../utils/auth';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';
import { ResponsiveTable, Column } from '../../components/mobile/ResponsiveTable';
import { OrderType } from '../../types';

// B2B order types for filtering
const B2B_ORDER_TYPES = [OrderType.B2B_DIRECT, OrderType.B2B_QUOTE];

interface UnifiedSale {
  id: string;
  receiptNumber: string;
  orderType: OrderType;
  customer?: {
    firstName: string;
    lastName: string;
  };
  reseller?: {
    id: string;
    firstName: string;
    lastName: string;
    company?: string;
  };
  pricingTier?: {
    name: string;
  };
  total: number;
  totalAmount?: number; // Alternative field name from unified order system
  paymentMethod: string;
  isRefunded: boolean;
  refundedAmount: number;
  createdAt: string;
  items?: Array<{
    margin?: number;
  }>;
  b2bMetadata?: {
    tierApplied?: string;
    totalSavings?: number;
  };
}

// Reseller interface for filter dropdown
interface Reseller {
  id: string;
  firstName: string;
  lastName: string;
  company?: string;
}

// Fetch resellers for filter dropdown
const fetchResellers = async (): Promise<Reseller[]> => {
  const response = await axiosInstance.get('/reseller-profiles');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

// Fetch B2B orders from the unified orders API
const fetchB2BOrders = async (
  locationId: string | null,
  page: number,
  pageSize: number,
  orderType?: string,
  resellerId?: string
) => {
  const params: Record<string, any> = {
    page: page + 1,
    limit: pageSize,
    // Always filter by B2B order types
    orderTypes: B2B_ORDER_TYPES.join(','),
  };

  if (locationId) {
    params.locationId = locationId;
  }

  // If a specific order type is selected, use it (must be B2B type)
  if (orderType && B2B_ORDER_TYPES.includes(orderType as OrderType)) {
    params.orderType = orderType;
    delete params.orderTypes; // Use single orderType filter instead
  }

  if (resellerId) {
    params.resellerId = resellerId;
  }

  // Use unified orders endpoint
  const response = await axiosInstance.get(`/orders`, { params });

  return {
    data: Array.isArray(response.data) ? response.data : response.data.data || [],
    total: response.data.total || (Array.isArray(response.data) ? response.data.length : 0),
  };
};

// Base filter options for B2B sales (reseller filter is added dynamically)
const baseFilterOptions: FilterOption[] = [
  {
    field: 'orderType',
    label: 'Order Type',
    type: 'select',
    options: [
      { value: OrderType.B2B_DIRECT, label: 'B2B Direct' },
      { value: OrderType.B2B_QUOTE, label: 'B2B Quote' },
    ],
  },
  {
    field: 'paymentMethod',
    label: 'Payment Method',
    type: 'select',
    options: [
      { value: 'cash', label: 'Cash' },
      { value: 'card', label: 'Card' },
      { value: 'mobile', label: 'Mobile' },
    ],
  },
  {
    field: 'refundStatus',
    label: 'Refund Status',
    type: 'select',
    options: [
      { value: 'refunded', label: 'Refunded' },
      { value: 'partial', label: 'Partial Refund' },
      { value: 'none', label: 'Not Refunded' },
    ],
  },
];

// Export columns configuration
const exportColumns: ColumnConfig[] = [
  { field: 'receiptNumber', headerName: 'Receipt #' },
  {
    field: 'orderType',
    headerName: 'Type',
    valueFormatter: (value) => {
      switch (value) {
        case OrderType.B2B_DIRECT:
          return 'B2B Direct';
        case OrderType.B2B_QUOTE:
          return 'B2B Quote';
        case OrderType.B2C:
          return 'B2C';
        default:
          return value || 'B2B';
      }
    },
  },
  {
    field: 'customerName',
    headerName: 'Customer/Reseller',
    valueFormatter: (_, row) => {
      if (row.reseller) {
        return row.reseller.company || `${row.reseller.firstName} ${row.reseller.lastName}`;
      }
      if (row.customer) {
        return `${row.customer.firstName} ${row.customer.lastName}`;
      }
      return 'Walk-in';
    },
  },
  {
    field: 'total',
    headerName: 'Total',
    valueFormatter: (value) => {
      const num = Number(value) || 0;
      return `${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DZD`;
    },
  },
  { field: 'paymentMethod', headerName: 'Payment Method' },
  {
    field: 'refundStatus',
    headerName: 'Refund Status',
    valueFormatter: (_, row) => {
      if (row.isRefunded) return 'Refunded';
      if (row.refundedAmount > 0) return 'Partial';
      return '';
    },
  },
  {
    field: 'createdAt',
    headerName: 'Date',
    valueFormatter: (value) => new Date(value).toLocaleString('fr-DZ'),
  },
];

// Get order type badge color
const getOrderTypeColor = (orderType: OrderType | undefined): string => {
  switch (orderType) {
    case OrderType.B2B_DIRECT:
      return palette.accent;
    case OrderType.B2B_QUOTE:
      return palette.warning;
    case OrderType.B2C:
    default:
      return palette.info;
  }
};

// Get order type icon
const getOrderTypeIcon = (orderType: OrderType | undefined) => {
  switch (orderType) {
    case OrderType.B2B_DIRECT:
      return <BusinessIcon sx={{ fontSize: 14 }} />;
    case OrderType.B2B_QUOTE:
      return <DescriptionIcon sx={{ fontSize: 14 }} />;
    case OrderType.B2C:
    default:
      return <BusinessIcon sx={{ fontSize: 14 }} />;
  }
};

// Get order type label
const getOrderTypeLabel = (orderType: OrderType | undefined): string => {
  switch (orderType) {
    case OrderType.B2B_DIRECT:
      return 'B2B DIRECT';
    case OrderType.B2B_QUOTE:
      return 'B2B QUOTE';
    case OrderType.B2C:
    default:
      return 'B2C';
  }
};

export const UnifiedSalesHistory = () => {
  const navigate = useNavigate();
  const { mode } = useContext(ColorModeContext);
  const user = getUser();
  const locationId = user?.locationId;
  const animations = useAnimationStyles();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [searchValue, setSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});

  // Fetch resellers for filter dropdown
  const { data: resellers } = useQuery({
    queryKey: ['resellers'],
    queryFn: fetchResellers,
  });

  // Dynamic filter options including reseller filter
  const filterOptions = useMemo((): FilterOption[] => {
    const resellerOptions = (resellers || []).map((reseller: Reseller) => ({
      value: reseller.id,
      label: reseller.company || `${reseller.firstName} ${reseller.lastName}`,
    }));

    return [
      ...baseFilterOptions,
      {
        field: 'resellerId',
        label: 'Reseller',
        type: 'select' as const,
        options: resellerOptions,
      },
    ];
  }, [resellers]);

  const { data, isLoading } = useQuery({
    queryKey: ['b2bOrders', locationId, page, pageSize, activeFilters.orderType, activeFilters.resellerId],
    queryFn: () => fetchB2BOrders(locationId || null, page, pageSize, activeFilters.orderType, activeFilters.resellerId),
  });

  const filteredData = useMemo(() => {
    if (!data?.data) return [];

    return data.data.filter((item: UnifiedSale) => {
      // Search filter
      if (searchValue) {
        const search = searchValue.toLowerCase();
        const customerName = item.customer
          ? `${item.customer.firstName} ${item.customer.lastName}`
          : '';
        const resellerName = item.reseller
          ? item.reseller.company || `${item.reseller.firstName} ${item.reseller.lastName}`
          : '';
        const matchesSearch =
          item.receiptNumber?.toLowerCase().includes(search) ||
          customerName.toLowerCase().includes(search) ||
          resellerName.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }

      // Order type filter (already applied at API level, but also filter locally)
      if (activeFilters.orderType && item.orderType !== activeFilters.orderType) {
        return false;
      }

      // Reseller filter (already applied at API level, but also filter locally)
      if (activeFilters.resellerId && item.reseller?.id !== activeFilters.resellerId) {
        return false;
      }

      // Payment method filter
      if (activeFilters.paymentMethod && item.paymentMethod !== activeFilters.paymentMethod) {
        return false;
      }

      // Refund status filter
      if (activeFilters.refundStatus) {
        const isRefunded = item.isRefunded === true;
        const hasPartialRefund = !isRefunded && item.refundedAmount > 0;
        const noRefund = !isRefunded && (!item.refundedAmount || item.refundedAmount === 0);

        if (activeFilters.refundStatus === 'refunded' && !isRefunded) return false;
        if (activeFilters.refundStatus === 'partial' && !hasPartialRefund) return false;
        if (activeFilters.refundStatus === 'none' && !noRefund) return false;
      }

      return true;
    });
  }, [data?.data, searchValue, activeFilters]);

  const paginatedData = useMemo(() => {
    const startIndex = page * pageSize;
    return filteredData.slice(startIndex, startIndex + pageSize);
  }, [filteredData, page, pageSize]);

  const handleFilterChange = (field: string, value: string) => {
    setActiveFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0); // Reset to first page when filter changes
  };

  const handleClearFilters = () => {
    setActiveFilters({});
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
    }).format(amount);
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('fr-DZ', { day: '2-digit', month: 'short', year: 'numeric' }),
      time: date.toLocaleTimeString('fr-DZ', { hour: '2-digit', minute: '2-digit' }),
    };
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method) {
      case 'cash':
        return <MoneyIcon sx={{ fontSize: 16 }} />;
      case 'card':
        return <CreditCardIcon sx={{ fontSize: 16 }} />;
      case 'mobile':
        return <PhoneAndroidIcon sx={{ fontSize: 16 }} />;
      default:
        return <PaymentIcon sx={{ fontSize: 16 }} />;
    }
  };

  const getPaymentMethodColor = (method: string): string => {
    switch (method) {
      case 'cash':
        return palette.success;
      case 'card':
        return palette.primary;
      case 'mobile':
        return palette.info;
      default:
        return palette.textMuted;
    }
  };

  // Calculate statistics including profit and type breakdown
  const stats = useMemo(() => {
    const total = filteredData.length;
    const totalRevenue = filteredData.reduce((sum: number, item: UnifiedSale) => sum + (item.total || item.totalAmount || 0), 0);

    // Count by order type
    const b2cSales = filteredData.filter((item: UnifiedSale) => item.orderType === OrderType.B2C).length;
    const b2bDirectSales = filteredData.filter((item: UnifiedSale) => item.orderType === OrderType.B2B_DIRECT).length;
    const b2bQuoteSales = filteredData.filter((item: UnifiedSale) => item.orderType === OrderType.B2B_QUOTE).length;

    // Calculate total profit from sale items margins
    const totalProfit = filteredData.reduce((sum: number, sale: UnifiedSale) => {
      const saleProfit = sale.items?.reduce((itemSum: number, item) =>
        itemSum + (item.margin || 0), 0) || 0;
      return sum + saleProfit;
    }, 0);

    // Calculate B2B savings
    const totalB2BSavings = filteredData.reduce((sum: number, sale: UnifiedSale) => {
      return sum + (sale.b2bMetadata?.totalSavings || 0);
    }, 0);

    return { total, totalRevenue, totalProfit, b2cSales, b2bDirectSales, b2bQuoteSales, totalB2BSavings };
  }, [filteredData]);

  // Helper to calculate profit for a single sale
  const calculateSaleProfit = (sale: UnifiedSale): number => {
    return sale.items?.reduce((sum: number, item) => sum + (item.margin || 0), 0) || 0;
  };

  // Get customer/reseller name
  const getCustomerName = (sale: UnifiedSale): string => {
    if (sale.reseller) {
      return sale.reseller.company || `${sale.reseller.firstName} ${sale.reseller.lastName}`;
    }
    if (sale.customer) {
      return `${sale.customer.firstName} ${sale.customer.lastName}`;
    }
    return 'Walk-in';
  };

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    borderLight: palette.borderLight,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  // Column definitions for ResponsiveTable
  const tableColumns: Column<UnifiedSale>[] = [
    {
      field: 'receiptNumber',
      headerName: 'Receipt #',
      render: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptIcon sx={{ fontSize: 18, color: palette.primary }} />
          <Typography
            variant="body2"
            sx={{
              fontFamily: typography.fontFamily.mono,
              color: colors.textPrimary,
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.bold,
              letterSpacing: typography.letterSpacing.wide,
            }}
          >
            {value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'orderType',
      headerName: 'Type',
      render: (value) => {
        const orderType = value as OrderType | undefined;
        const typeColor = getOrderTypeColor(orderType);
        return (
          <Chip
            icon={getOrderTypeIcon(orderType)}
            label={getOrderTypeLabel(orderType)}
            size="small"
            sx={{
              backgroundColor: alpha(typeColor, 0.1),
              color: typeColor,
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.bold,
              border: `1px solid ${typeColor}`,
              '& .MuiChip-icon': {
                color: typeColor,
              },
            }}
          />
        );
      },
    },
    {
      field: 'customer',
      headerName: 'Customer/Reseller',
      render: (_, row) => {
        const isB2B = row.orderType === OrderType.B2B_DIRECT || row.orderType === OrderType.B2B_QUOTE;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isB2B ? (
              <BusinessIcon sx={{ fontSize: 18, color: palette.accent }} />
            ) : (
              <PersonIcon sx={{ fontSize: 18, color: palette.info }} />
            )}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: typography.fontFamily.display,
                  fontWeight: typography.fontWeight.semibold,
                  color: colors.textPrimary,
                  fontSize: typography.fontSize.sm,
                }}
              >
                {getCustomerName(row)}
              </Typography>
              {isB2B && row.pricingTier && (
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    color: palette.accent,
                    fontSize: typography.fontSize.xs,
                    textTransform: 'uppercase',
                  }}
                >
                  {row.pricingTier.name || row.b2bMetadata?.tierApplied}
                </Typography>
              )}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'total',
      headerName: 'Total',
      render: (value, row) => (
        <Box
          sx={{
            display: 'inline-flex',
            px: 2,
            py: 0.75,
            borderRadius: theme.radius.xs,
            backgroundColor: alpha(palette.success, 0.1),
            border: `1px solid ${palette.success}`,
          }}
        >
          <Typography
            sx={{
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.extrabold,
              color: palette.success,
            }}
          >
            {formatCurrency(value || row.totalAmount || 0)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'profit',
      headerName: 'Profit',
      render: (_, row) => {
        const profit = calculateSaleProfit(row);
        const hasProfit = profit !== 0;
        const profitColor = profit >= 0 ? palette.success : palette.error;
        return (
          <Box
            sx={{
              display: 'inline-flex',
              px: 2,
              py: 0.75,
              borderRadius: theme.radius.xs,
              backgroundColor: alpha(profitColor, 0.1),
              border: `1px solid ${profitColor}`,
            }}
          >
            <Typography
              sx={{
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.extrabold,
                color: hasProfit ? profitColor : colors.textMuted,
              }}
            >
              {hasProfit ? (profit > 0 ? '+' : '') + formatCurrency(profit) : '-'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'paymentMethod',
      headerName: 'Payment',
      render: (value) => {
        const paymentColor = getPaymentMethodColor(value);
        return (
          <Chip
            icon={getPaymentMethodIcon(value)}
            label={value?.toUpperCase() || 'N/A'}
            sx={{
              backgroundColor: alpha(paymentColor, 0.1),
              color: paymentColor,
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.bold,
              border: `1px solid ${paymentColor}`,
              textTransform: 'uppercase',
              '& .MuiChip-icon': {
                color: paymentColor,
              },
            }}
          />
        );
      },
    },
    {
      field: 'refundStatus',
      headerName: 'Refund',
      render: (_, row) => {
        if (row.isRefunded) {
          return (
            <Chip
              icon={<UndoIcon sx={{ fontSize: 14 }} />}
              label="REFUNDED"
              size="small"
              sx={{
                backgroundColor: alpha(palette.error, 0.1),
                color: palette.error,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.bold,
                border: `1px solid ${palette.error}`,
                '& .MuiChip-icon': {
                  color: palette.error,
                },
              }}
            />
          );
        }
        if (row.refundedAmount > 0) {
          return (
            <Chip
              icon={<UndoIcon sx={{ fontSize: 14 }} />}
              label="PARTIAL"
              size="small"
              sx={{
                backgroundColor: alpha(palette.warning, 0.1),
                color: palette.warning,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.bold,
                border: `1px solid ${palette.warning}`,
                '& .MuiChip-icon': {
                  color: palette.warning,
                },
              }}
            />
          );
        }
        return null;
      },
    },
    {
      field: 'createdAt',
      headerName: 'Date',
      render: (value) => {
        const saleDate = formatDateTime(value);
        return (
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.semibold,
                color: colors.textPrimary,
                fontSize: typography.fontSize.sm,
                lineHeight: 1.2,
              }}
            >
              {saleDate.date}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontFamily: typography.fontFamily.mono,
                color: colors.textMuted,
                fontSize: typography.fontSize.xs,
              }}
            >
              {saleDate.time}
            </Typography>
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Stats Dashboard */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2, mb: 3, ...animations.slideUp(0.1) }}>
        <Card
          sx={{
            backgroundColor: colors.surface,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.sm,
            p: 2.5,
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.primary,
            },
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 24px ${alpha(palette.primary, 0.15)}`,
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: colors.textMuted,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.widest,
                  fontWeight: typography.fontWeight.bold,
                }}
              >
                TOTAL SALES
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.extrabold,
                  color: colors.textPrimary,
                  lineHeight: 1,
                  mt: 1,
                }}
              >
                {stats.total}
              </Typography>
            </Box>
            <ShoppingCartIcon sx={{ fontSize: 40, color: alpha(palette.primary, 0.2) }} />
          </Box>
        </Card>

        <Card
          sx={{
            backgroundColor: colors.surface,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.sm,
            p: 2.5,
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.success,
            },
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 24px ${alpha(palette.success, 0.15)}`,
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: colors.textMuted,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.widest,
                  fontWeight: typography.fontWeight.bold,
                }}
              >
                REVENUE
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.extrabold,
                  color: palette.success,
                  lineHeight: 1,
                  mt: 1,
                  fontSize: typography.fontSize.lg,
                }}
              >
                {formatCurrency(stats.totalRevenue)}
              </Typography>
            </Box>
            <TrendingUpIcon sx={{ fontSize: 40, color: alpha(palette.success, 0.2) }} />
          </Box>
        </Card>

        <Card
          sx={{
            backgroundColor: colors.surface,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.sm,
            p: 2.5,
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.info,
            },
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 24px ${alpha(palette.info, 0.15)}`,
            },
          }}
        >
          <Typography
            variant="overline"
            sx={{
              color: colors.textMuted,
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
              letterSpacing: typography.letterSpacing.widest,
              fontWeight: typography.fontWeight.bold,
            }}
          >
            B2C RETAIL
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontFamily: typography.fontFamily.mono,
              fontWeight: typography.fontWeight.extrabold,
              color: palette.info,
              lineHeight: 1,
              mt: 1,
            }}
          >
            {stats.b2cSales}
          </Typography>
        </Card>

        <Card
          sx={{
            backgroundColor: colors.surface,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.sm,
            p: 2.5,
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.accent,
            },
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 24px ${alpha(palette.accent, 0.15)}`,
            },
          }}
        >
          <Typography
            variant="overline"
            sx={{
              color: colors.textMuted,
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
              letterSpacing: typography.letterSpacing.widest,
              fontWeight: typography.fontWeight.bold,
            }}
          >
            B2B DIRECT
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontFamily: typography.fontFamily.mono,
              fontWeight: typography.fontWeight.extrabold,
              color: palette.accent,
              lineHeight: 1,
              mt: 1,
            }}
          >
            {stats.b2bDirectSales}
          </Typography>
        </Card>

        <Card
          sx={{
            backgroundColor: colors.surface,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.sm,
            p: 2.5,
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.warning,
            },
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 24px ${alpha(palette.warning, 0.15)}`,
            },
          }}
        >
          <Typography
            variant="overline"
            sx={{
              color: colors.textMuted,
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
              letterSpacing: typography.letterSpacing.widest,
              fontWeight: typography.fontWeight.bold,
            }}
          >
            B2B QUOTE
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontFamily: typography.fontFamily.mono,
              fontWeight: typography.fontWeight.extrabold,
              color: palette.warning,
              lineHeight: 1,
              mt: 1,
            }}
          >
            {stats.b2bQuoteSales}
          </Typography>
        </Card>
      </Box>

      {/* Search & Filter Bar */}
      <Card
        sx={{
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          p: 2,
          mb: 3,
          ...animations.slideUp(0.15),
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="SEARCH BY RECEIPT, CUSTOMER, OR RESELLER..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            sx={{
              flex: 1,
              minWidth: 300,
              '& .MuiOutlinedInput-root': {
                backgroundColor: colors.surfaceElevated,
                border: `1px solid ${colors.border}`,
                borderRadius: theme.radius.sm,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.sm,
                '& fieldset': { border: 'none' },
                '&:hover': {
                  borderColor: colors.borderLight,
                },
                '&.Mui-focused': {
                  borderColor: palette.primary,
                },
              },
              '& .MuiInputBase-input': {
                color: colors.textPrimary,
                fontWeight: typography.fontWeight.medium,
                textTransform: 'uppercase',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.textMuted }} />
                </InputAdornment>
              ),
            }}
          />
          <TableToolbar
            title=""
            searchValue=""
            onSearchChange={() => {}}
            searchPlaceholder=""
            filters={filterOptions}
            activeFilters={activeFilters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            data={filteredData}
            columns={exportColumns}
            exportFileName="unified_sales_history"
            hideSearch
          />
        </Box>
      </Card>

      {/* Sales Table */}
      <Card
        sx={{
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          overflow: 'hidden',
          ...animations.slideUp(0.2),
        }}
      >
        <ResponsiveTable
          columns={tableColumns}
          rows={paginatedData}
          loading={isLoading}
          emptyMessage="No sales found"
          primaryField="receiptNumber"
          getPrimaryText={(row) => row.receiptNumber || `Sale #${row.id?.slice(0, 8) || 'Unknown'}`}
          getSecondaryText={(row) => getCustomerName(row)}
          onRowClick={(sale) => navigate(`/app/sales/show/${sale.id}`)}
        />

        {/* Industrial Pagination */}
        <Box
          sx={{
            backgroundColor: colors.surfaceElevated,
            borderTop: `2px solid ${colors.border}`,
          }}
        >
          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={pageSize}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{
              '& .MuiTablePagination-toolbar': {
                color: colors.textPrimary,
                fontFamily: typography.fontFamily.mono,
              },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.bold,
                textTransform: 'uppercase',
                letterSpacing: typography.letterSpacing.wide,
              },
              '& .MuiSelect-select': {
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.bold,
              },
            }}
          />
        </Box>
      </Card>
    </Box>
  );
};

export default UnifiedSalesHistory;
